import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import TextField from '@material-ui/core/TextField';
import FormDivider from 'components/FormDivider';
import { getService } from 'reducers/service';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import Content from '../Content';
import { setCircularLoading } from 'reducers/UiReducer';
import { notify } from 'reducers/NotifierReducer';
import {
  AGENCY_COLORED,
  AGENCY_GRAYED,
  DEPARTMENT_COLORED,
  DEPARTMENT_GRAYED,
} from 'utils/constants';
import { getImageMimeType } from 'utils/functions';

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(2),
    '& > h5': {
      marginBottom: 20,
    },
  },

  textField: {
    margin: 10,
    width: '100%',
    maxWidth: '500px',
  },

  checkBox: {
    marginRight: 50,
  },

  errorText: {
    color: 'red',
    fontSize: '0.875rem',
  },

  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(2),
    paddingRight: theme.spacing(3),
  },

  imagePreviewContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  dialogImagePreview: {
    width: '100%',
    maxHeight: '400px',
    objectFit: 'contain',
  },

  pointer: {
    cursor: 'pointer',
  },
}));

const AgencySettings = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const agencyId = 1;

  const agencyService = getService('pic-agency');
  const agencyTipLineService = getService('pic-agency-tipline');
  const agencyBadgeService = getService('pic-agency-badge');

  const [agencyName, setAgencyName] = useState('');
  const [department, setDepartment] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [tipLines, setTipLines] = useState([]);
  const [agencyBadges, setAgencyBadges] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [errors, setErrors] = useState({
    agencyName: '',
    department: '',
    address: '',
    phone: '',
  });

  const handleTipLineChange = (id, field, value) => {
    setTipLines((prev) => prev.map((tip) => (tip.Id === id ? { ...tip, [field]: value } : tip)));
  };

  useEffect(() => {
    fetchAgencyData();
  }, [agencyId]);

  const fetchAgencyData = async () => {
    try {
      dispatch(setCircularLoading(true));
      const agency = await agencyService.get(1);
      const agencyTipline = await agencyTipLineService.find();
      const agencyBadgeData = await agencyBadgeService.find();
      setAgencyData(agency, agencyTipline, agencyBadgeData);
    } catch (error) {
      console.error('error while fetching agency settings', error);
      dispatch(notify('Error Fetching Agency Settings', 'error'));
    } finally {
      dispatch(setCircularLoading(false));
    }
  };

  const setAgencyData = (agency, agencyTipline, agencyBadgeData) => {
    if (!agency) return;
    const { Name, DepartmentName, Phone, Address } = agency;
    setAgencyName(Name || '');
    setDepartment(DepartmentName || '');
    setAddress(Address || '');
    setPhone(Phone || '');
    const { data } = agencyTipline;
    setTipLines(data);
    setAgencyBadges(agencyBadgeData?.data || []);
  };

  const handleSave = () => {
    const newErrors = {};

    if (!agencyName) newErrors.agencyName = 'Agency Name is required';
    if (agencyName.length > 255)
      newErrors.agencyName = 'Agency Name can be a maximum of 255 characters';

    if (!department) newErrors.department = 'Department Name is required';
    if (department.length > 255)
      newErrors.department = 'Department Name can be a maximum of 255 characters';

    if (!phone) newErrors.phone = 'Phone is required';
    if (phone.length !== 10) newErrors.phone = 'Phone number should be of 10 digits';

    if (!address) newErrors.address = 'Address is required';
    if (address.length > 255) newErrors.address = 'Address can be a maximum of 255 characters';

    tipLines.forEach((tip, index) => {
      const isPrimary = tip.IsPrimary;

      // Primary TipLine (Mandatory)
      if (isPrimary) {
        if (!tip.TipLineNumber) {
          newErrors[`TipLineNumber_${index}`] = 'Primary Tip Line Number is required';
        } else if (tip.TipLineNumber.length !== 10) {
          newErrors[`TipLineNumber_${index}`] = 'Tip Line Number should be of 10 digits';
        }

        if (!tip.TipLineRegion) {
          newErrors[`TipLineRegion_${index}`] = 'Primary Tip Line Region is required';
        } else if (tip.TipLineRegion.length > 255) {
          newErrors[`TipLineRegion_${index}`] =
            'Tip Line Region can be a maximum of 255 characters';
        }
      }

      // Other TipLines (Optional, but must be within limit if filled)
      else {
        const hasNumber = !!tip.TipLineNumber;
        const hasRegion = !!tip.TipLineRegion;

        // Validate if either one is filled and the other is missing
        if (hasNumber && !hasRegion) {
          newErrors[`TipLineRegion_${index}`] =
            'Tip Line Region is required if Tip Line Number is provided';
        }
        if (hasRegion && !hasNumber) {
          newErrors[`TipLineNumber_${index}`] =
            'Tip Line Number is required if Tip Line Region is provided';
        }

        // Validate max length if they are filled
        if (hasNumber && tip.TipLineNumber.length !== 10) {
          newErrors[`TipLineNumber_${index}`] = 'Tip Line Number should be of 10 digits';
        }
        if (hasRegion && tip.TipLineRegion.length > 255) {
          newErrors[`TipLineRegion_${index}`] =
            'Tip Line Region can be a maximum of 255 characters';
        }
      }
    });

    if (Object.keys(newErrors).length === 0) {
      const updatedAgency = {
        Name: agencyName,
        DepartmentName: department,
        Phone: phone,
        Address: address,
      };

      dispatch(setCircularLoading(true));

      agencyService
        .patch(agencyId, updatedAgency)
        .then(() => {
          const tipLineRequests = tipLines.map((tip) => {
            return agencyTipLineService.patch(tip.Id, {
              TipLineNumber: tip.TipLineNumber,
              TipLineRegion: tip.TipLineRegion,
            });
          });

          return Promise.all(tipLineRequests);
        })
        .then(() => {
          props.notifyDataUpdate({ type: 'edit-agency', data: { agencyId } });
          dispatch(notify('Successfully Saved Agency Settings', 'success'));
        })
        .catch((err) => {
          console.error(err);
          dispatch(notify('Error Saving Agency Settings', 'error'));
        })
        .finally(() => {
          dispatch(setCircularLoading(false));
        });
    }

    setErrors(newErrors);
  };

  const getBadgeLabel = (imageType) => {
    if (imageType === AGENCY_COLORED) {
      return 'Agency Colored Badge';
    } else if (imageType === AGENCY_GRAYED) {
      return 'Agency Grayed Badge';
    } else if (imageType === DEPARTMENT_COLORED) {
      return 'Department Colored Badge';
    } else if (imageType === DEPARTMENT_GRAYED) {
      return 'Department Grayed Badge';
    } else {
      return '';
    }
  };

  const handleFileChange = (event, image) => {
    const file = event.target.files[0];

    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      dispatch(notify('Only JPEG, JPG and PNG formats are allowed.', 'error'));
      return;
    }

    if (file.size > maxSize) {
      dispatch(notify('File size exceeds 5 MB. Please upload a smaller file.', 'error'));
      return;
    }
    setSelectedFile(file);
    setSelectedImage(image);
    setPreviewURL(URL.createObjectURL(file));
    setOpenDialog(true);
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setPreviewURL(null);
    setSelectedImage(null);
  };

  const handleImageSave = async () => {
    if (!selectedFile) return;

    dispatch(setCircularLoading(true));

    const reader = new FileReader();

    reader.readAsDataURL(selectedFile);

    reader.onloadend = async () => {
      try {
        const base64String = reader.result.split(',')[1];

        const updatedAgencyBadge = {
          FileName: selectedFile.name,
          FileData: base64String,
        };

        await agencyBadgeService.patch(selectedImage.Id, updatedAgencyBadge);

        dispatch(notify('Successfully Saved Image', 'success'));
      } catch (error) {
        console.error('Error uploading image:', error);
        dispatch(notify('Error Saving Image', 'error'));
      } finally {
        dispatch(setCircularLoading(false));

        handleCancel();

        await fetchAgencyData();
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);

      dispatch(notify('Error Reading File', 'error'));

      dispatch(setCircularLoading(false));

      handleCancel();
    };
  };

  const handlePreviewImage = (image) => {
    setCurrentImage(image);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setCurrentImage(null);
  };

  return (
    <Content>
      <Grid container className={classes.wrap}>
        <Grid item xs={12}>
          <div className={classes.wrap}>
            <FormDivider title="Agency Information" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Agency Name"
                  variant="outlined"
                  value={agencyName}
                  onChange={(ev) => setAgencyName(ev.target.value)}
                  size="small"
                  error={!!errors.agencyName}
                  helperText={errors.agencyName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Agency Department"
                  variant="outlined"
                  value={department}
                  onChange={(ev) => setDepartment(ev.target.value)}
                  size="small"
                  error={!!errors.department}
                  helperText={errors.department}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Address"
                  variant="outlined"
                  value={address}
                  onChange={(ev) => setAddress(ev.target.value)}
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.textField}
                  label="Phone"
                  variant="outlined"
                  value={phone}
                  onChange={(ev) => setPhone(ev.target.value)}
                  size="small"
                  type="number"
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.wrap}>
            <FormDivider title="Escape Alert Report Information" />
            <Grid container spacing={2}>
              {tipLines.map((tip, index) => (
                <React.Fragment key={tip.Id}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      label={tip.IsPrimary ? 'Primary Tip Line Number' : 'Other Tip Line Number'}
                      variant="outlined"
                      value={tip.TipLineNumber}
                      onChange={(e) => handleTipLineChange(tip.Id, 'TipLineNumber', e.target.value)}
                      size="small"
                      error={!!errors[`TipLineNumber_${index}`]}
                      helperText={errors[`TipLineNumber_${index}`] || ''}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.textField}
                      label={tip.IsPrimary ? 'Primary Tip Line Region' : 'Other Tip Line Region'}
                      variant="outlined"
                      value={tip.TipLineRegion}
                      onChange={(e) => handleTipLineChange(tip.Id, 'TipLineRegion', e.target.value)}
                      size="small"
                      error={!!errors[`TipLineRegion_${index}`]}
                      helperText={errors[`TipLineRegion_${index}`] || ''}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.wrap}>
            <FormDivider title="Agency Badges" />
            <Grid container spacing={2}>
              {agencyBadges.map((image) => (
                <Grid item xs={12} sm={6} key={image.id}>
                  <Typography variant="subtitle1">{getBadgeLabel(image.ImageType)}</Typography>
                  {image.FileData ? (
                    <div className={classes.imagePreviewContainer}>
                      <img
                        src={`data:${getImageMimeType(currentImage?.FileName)};base64,${
                          image.FileData
                        }`}
                        alt={image.FileName}
                        width="50"
                        height="50"
                        className={classes.pointer}
                        onClick={() => handlePreviewImage(image)}
                      />
                      <Typography
                        variant="body2"
                        onClick={() => handlePreviewImage(image)}
                        className={classes.pointer}>
                        {image.FileName}
                      </Typography>
                      <IconButton color="primary" component="label">
                        <EditIcon />
                        <input
                          type="file"
                          accept="image/png, image/jpeg"
                          hidden
                          onChange={(e) => handleFileChange(e, image)}
                        />
                      </IconButton>
                    </div>
                  ) : (
                    <div className={classes.imagePreviewContainer}>
                      <Typography variant="body2" color="textSecondary">
                        No Image Present
                      </Typography>
                      <IconButton color="primary" component="label">
                        <AddIcon />
                        <input
                          type="file"
                          accept="image/png, image/jpeg"
                          hidden
                          onChange={(e) => handleFileChange(e, image)}
                        />
                      </IconButton>
                    </div>
                  )}
                </Grid>
              ))}
            </Grid>
          </div>
        </Grid>
      </Grid>
      <Grid container className={classes.buttonContainer}>
        <Button onClick={handleSave} color="primary" variant="contained" startIcon={<SaveIcon />}>
          Save
        </Button>
      </Grid>

      <Dialog open={openDialog} onClose={handleCancel}>
        <DialogTitle>Preview Image</DialogTitle>
        <DialogContent>
          {previewURL && (
            <img src={previewURL} alt="Preview" className={classes.dialogImagePreview} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleImageSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={handlePreviewClose}>
        <DialogTitle>{`Preview Image - ${currentImage?.FileName}`}</DialogTitle>
        <DialogContent>
          {
            <img
              src={`data:${getImageMimeType(currentImage?.FileName)};base64,${
                currentImage?.FileData
              }`}
              alt="Preview"
              className={classes.dialogImagePreview}
            />
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};

const mapStateToProps = (state) => {
  return {
    //dictionary: state.dictionary
  };
};

export default connect(mapStateToProps, { notifyDataUpdate })(AgencySettings);
